package fyi.moko.hotpl.ui.map

import androidx.compose.foundation.layout.Box
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInWindow
import androidx.compose.ui.platform.LocalDensity
import fyi.moko.hotpl.domain.model.Place
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import kotlin.js.Promise

/**
 * Compose (Skiko canvas) can't host DOM widgets inline, so the web map is a Google Maps
 * (Maps JavaScript API) map rendered into an absolutely-positioned `<div>` overlaid on top
 * of the canvas. A [WebMapController] owns that div + the map; [WebMapBox] lays out an empty
 * Compose slot and keeps the div's screen rect in sync with the slot via
 * `onGloballyPositioned`. The API script is loaded once.
 *
 * ── 왜 Leaflet + OSM을 버리는가 (2026-10-04) ──────────────────────────────
 * 세 플랫폼 지도를 같은 [WARM_MAP_STYLE_JSON]으로 맞추기로 했고, Directory가 Google Places
 * 데이터를 쓰므로 비(非)구글 지도 위에 올리면 Maps Platform 약관에 걸린다. OSM 공용 타일
 * 서버도 상용 트래픽용이 아니다.
 *
 * 마커는 `google.maps.Marker`(레거시)다. AdvancedMarkerElement는 mapId를 요구하고, mapId가
 * 있으면 JSON `styles`가 무시된다 — 스타일을 코드 한 곳에 두려면 이 조합이다.
 */

/**
 * "HOTPLA Web Maps" key (GCP hotpla-app). Restricted to the Maps JavaScript API and to the
 * hotpla.moko.fyi / localhost:8080 referrers, so — like the Firebase web apiKey in Main.kt —
 * it is safe to ship in the bundle.
 */
private const val WEB_MAPS_API_KEY = "AIzaSyDmdF6sni4DJ3MCzPxMmhg7inVyJyTyFlc"

private var mapsPromise: Promise<Unit>? = null

private fun ensureGoogleMaps(): Promise<Unit> {
    mapsPromise?.let { return it }
    val p = Promise<Unit> { resolve, reject ->
        window.asDynamic().__hotplMapsReady = { resolve(Unit) }
        val script = document.createElement("script")
        script.setAttribute(
            "src",
            "https://maps.googleapis.com/maps/api/js?key=$WEB_MAPS_API_KEY&v=weekly&callback=__hotplMapsReady",
        )
        script.setAttribute("async", "")
        script.addEventListener("error", { _: Event -> reject(RuntimeException("Google Maps failed to load")) })
        document.head!!.appendChild(script)
    }
    mapsPromise = p
    return p
}

/**
 * @param styled the journal map wears [WARM_MAP_STYLE_JSON]; the location picker keeps the
 *   plain Google map (POIs help find the spot you mean), same as Android and iOS.
 */
internal class WebMapController(private val interactive: Boolean, private val styled: Boolean = true) {
    val div: HTMLElement = (document.createElement("div") as HTMLElement).apply {
        style.setProperty("position", "fixed")
        style.setProperty("z-index", "1")
        style.setProperty("display", "none")
        style.setProperty("background", "#F7F3EC")
        // A display-only map ignores pointer input so it can't trap the page's scroll.
        style.setProperty("pointer-events", if (interactive) "auto" else "none")
    }

    // Google Maps rewrites its container's `position` to `relative`, which would pull [div]
    // out of its fixed overlay slot — so the map lives in an inner element that fills it.
    private val mapElement: HTMLElement = (document.createElement("div") as HTMLElement).apply {
        style.setProperty("width", "100%")
        style.setProperty("height", "100%")
        div.appendChild(this)
    }

    private var g: dynamic = null
    var map: dynamic = null
        private set
    private val placeMarkers = mutableListOf<dynamic>()
    private var pickMarker: dynamic = null
    private var nameWindow: dynamic = null

    private val readyState = mutableStateOf(false)
    val ready: Boolean get() = readyState.value

    fun attach() {
        document.body!!.appendChild(div)
        ensureGoogleMaps().then {
            val maps = window.asDynamic().google.maps
            g = maps
            val opts = js("{}")
            opts.center = latLng(MapDefaults.FALLBACK_TARGET.latitude, MapDefaults.FALLBACK_TARGET.longitude)
            opts.zoom = MapDefaults.DEFAULT_ZOOM.toInt()
            opts.disableDefaultUI = true
            opts.zoomControl = interactive
            opts.clickableIcons = false
            opts.keyboardShortcuts = interactive
            opts.gestureHandling = if (interactive) "greedy" else "none"
            if (styled) opts.styles = JSON.parse(WARM_MAP_STYLE_JSON)
            val container = mapElement
            map = js("new maps.Map(container, opts)")
            readyState.value = true
            Unit
        }.catch { e ->
            console.error("Google Maps init failed", e)
            Unit
        }
    }

    fun detach() {
        nameWindow?.close()
        placeMarkers.forEach { it.setMap(null) }
        placeMarkers.clear()
        pickMarker?.setMap(null)
        if (div.parentElement != null) div.remove()
    }

    fun syncBounds(x: Float, y: Float, w: Float, h: Float) {
        div.style.setProperty("left", "${x}px")
        div.style.setProperty("top", "${y}px")
        div.style.setProperty("width", "${w}px")
        div.style.setProperty("height", "${h}px")
        div.style.setProperty("display", "block")
    }

    private fun latLng(lat: Double, lng: Double): dynamic {
        val ll = js("{}")
        ll.lat = lat
        ll.lng = lng
        return ll
    }

    /**
     * Two-step, as before: the first tap selects and names the place in a small info
     * window, and tapping the already-selected marker opens it. HomeScreen's
     * SelectedPlaceBanner can't do the naming here — it is drawn on the Compose canvas,
     * which this DOM overlay covers (Leaflet used a permanent tooltip for the same reason).
     */
    fun setPlaceMarkers(
        located: List<Place>,
        selectedId: String?,
        pinColors: Map<String, Color>,
        wantToGoIds: Set<String>,
        onSelect: (String) -> Unit,
        onOpen: (String) -> Unit,
    ) {
        val maps = g ?: return
        val m = map ?: return
        placeMarkers.forEach { it.setMap(null) }
        placeMarkers.clear()
        nameWindow?.close()
        located.forEach { place ->
            val selected = place.id == selectedId
            // 선택된 핀은 **더 크게** 세운다. 색은 감정의 것이라 선택 표시로 쓸 수 없다(R-8).
            val height = if (selected) SELECTED_PIN_HEIGHT_PX else PIN_HEIGHT_PX
            val width = height * 14 / 20
            val svg = pinSvg(
                color = (pinColors[place.id] ?: NEUTRAL_PIN_COLOR).toCssHex(),
                hollow = place.id in wantToGoIds,
                width = width,
                height = height,
            )
            val icon = js("{}")
            icon.url = "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg)
            icon.scaledSize = js("new maps.Size(width, height)")
            // 끝이 곧 좌표다 — 앵커를 아래 가운데에 둔다.
            icon.anchor = js("new maps.Point(width / 2, height)")
            val markerOpts = js("{}")
            markerOpts.position = latLng(place.latitude!!, place.longitude!!)
            markerOpts.map = m
            markerOpts.icon = icon
            markerOpts.title = place.name
            markerOpts.zIndex = if (selected) 1000 else 0
            val marker = js("new maps.Marker(markerOpts)")
            marker.addListener("click") { _: dynamic ->
                if (selected) onOpen(place.id) else onSelect(place.id)
            }
            placeMarkers.add(marker)
            if (selected) showName(maps, m, marker, place.name)
        }
    }

    private fun showName(maps: dynamic, m: dynamic, marker: dynamic, name: String) {
        if (nameWindow == null) nameWindow = js("new maps.InfoWindow({ headerDisabled: true })")
        val window = nameWindow
        val label = document.createElement("div") as HTMLElement
        label.textContent = name
        label.style.setProperty("font", "600 14px system-ui, sans-serif")
        label.style.setProperty("color", "#3A2E25")
        label.style.setProperty("padding", "2px 4px")
        window.setContent(label)
        val opts = js("{}")
        opts.anchor = marker
        opts.map = m
        opts.shouldFocus = false
        window.open(opts)
    }

    /** [zoom] null re-centers without touching the current zoom level. */
    fun centerOn(lat: Double, lng: Double, zoom: Int?) {
        val m = map ?: return
        m.panTo(latLng(lat, lng))
        if (zoom != null) m.setZoom(zoom)
    }

    /** Single draggable pin for the location picker; tapping the map also moves it. */
    fun setPickMarker(lat: Double?, lng: Double?, onPick: (Double, Double) -> Unit) {
        val maps = g ?: return
        val m = map ?: return
        if (lat == null || lng == null) {
            pickMarker?.setMap(null)
            pickMarker = null
            return
        }
        val existing = pickMarker
        if (existing == null) {
            val opts = js("{}")
            opts.position = latLng(lat, lng)
            opts.map = m
            opts.draggable = true
            val marker = js("new maps.Marker(opts)")
            marker.addListener("dragend") { _: dynamic ->
                val ll = marker.getPosition()
                onPick(ll.lat() as Double, ll.lng() as Double)
            }
            pickMarker = marker
        } else {
            existing.setPosition(latLng(lat, lng))
        }
    }

    fun onMapClick(onPick: (Double, Double) -> Unit) {
        map?.addListener("click") { e: dynamic -> onPick(e.latLng.lat() as Double, e.latLng.lng() as Double) }
    }
}

private external fun encodeURIComponent(value: String): String

@Composable
internal fun rememberWebMapController(interactive: Boolean, styled: Boolean = true): WebMapController {
    val controller = remember(interactive, styled) { WebMapController(interactive, styled) }
    DisposableEffect(controller) {
        controller.attach()
        onDispose { controller.detach() }
    }
    return controller
}

/**
 * An empty Compose slot whose on-screen rectangle drives the position/size of the
 * map div. Compose layout px are converted to CSS px via the current density.
 */
@Composable
internal fun WebMapBox(controller: WebMapController, modifier: Modifier) {
    val density = LocalDensity.current.density
    Box(
        modifier = modifier.onGloballyPositioned { coords ->
            val pos = coords.positionInWindow()
            val size = coords.size
            controller.syncBounds(
                x = pos.x / density,
                y = pos.y / density,
                w = size.width / density,
                h = size.height / density,
            )
        },
    )
}

/**
 * 물방울 핀 — Android [PlacePin]과 같은 Material "place" 실루엣, 같은 이유(0-26): 원은 지도의
 * 기본 POI 점과 구별되지 않는다. 그림자·흰 테두리·머리의 흰 점까지 같게 맞춘다.
 * [hollow]는 안 가본 곳(R-15): 채움과 테두리를 뒤집는다 — Android `PlacePin`과 같은 규칙.
 */
private fun pinSvg(color: String, hollow: Boolean, width: Int, height: Int): String {
    val fill = if (hollow) "#fff" else color
    val edge = if (hollow) color else "#fff"
    val edgeWidth = if (hollow) "2" else "1.4"
    return """<svg xmlns="http://www.w3.org/2000/svg" width="$width" height="$height" viewBox="-1 -1 16 22">""" +
        """<path d="$PIN_PATH" fill="#000" fill-opacity=".28" transform="translate(.6 .9)"/>""" +
        """<path d="$PIN_PATH" fill="$fill" stroke="$edge" stroke-width="$edgeWidth"/>""" +
        """<circle cx="7" cy="7" r="2.6" fill="$edge"/></svg>"""
}

/** Material "place" glyph shifted to span x 0..14, y 0..20 — the same numbers as Android. */
private const val PIN_PATH =
    "M7 0C3.13 0 0 3.13 0 7c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
private const val PIN_HEIGHT_PX = 40
private const val SELECTED_PIN_HEIGHT_PX = 52

/** 마커 SVG는 CSS 색 문자열을 받는다 — Compose [Color]에는 그런 표현이 없어 직접 만든다. */
private fun Color.toCssHex(): String {
    fun channel(value: Float) = (value * 255).toInt().coerceIn(0, 255).toString(16).padStart(2, '0')
    return "#" + channel(red) + channel(green) + channel(blue)
}
